import { Skeleton } from "@/components/ui/skeleton";

export function StatCardSkeleton() {
  return (
    <div className="glass-card p-5">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-3 w-20" />
        </div>
        <Skeleton className="h-9 w-9 rounded-xl" />
      </div>
    </div>
  );
}

export function ComplianceGaugeSkeleton() {
  return (
    <div className="glass-card flex flex-col items-center justify-center p-6">
      <Skeleton className="h-3 w-32 mb-4" />
      <Skeleton className="h-44 w-44 rounded-full" />
      <Skeleton className="h-4 w-24 mt-3" />
    </div>
  );
}

export function ChartSkeleton() {
  return (
    <div className="glass-card p-5 lg:col-span-2">
      <Skeleton className="h-3 w-40 mb-4" />
      <div className="h-52 flex items-end gap-2 px-4">
        {[40, 65, 30, 80, 55, 70, 45].map((h, i) => (
          <Skeleton key={i} className="flex-1 rounded-t-md" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}

export function ActivityFeedSkeleton() {
  return (
    <div className="glass-card p-5 lg:col-span-2">
      <Skeleton className="h-3 w-44 mb-4" />
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-start gap-3">
            <Skeleton className="h-7 w-7 rounded-lg shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CalendarSkeleton() {
  return (
    <div className="glass-card p-5">
      <Skeleton className="h-3 w-36 mb-4" />
      <div className="space-y-2.5">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg bg-secondary/50 p-2.5">
            <Skeleton className="h-10 w-12 rounded-lg" />
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RiskDashboardSkeleton() {
  return (
    <div className="glass-card p-5">
      <Skeleton className="h-3 w-40 mb-4" />
      <Skeleton className="h-5 w-48 mb-5" />
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-xl bg-secondary/50 p-4">
        <div className="flex flex-col items-center gap-2">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-8 w-28" />
        </div>
        <Skeleton className="h-8 w-8 rounded-full" />
        <div className="flex flex-col items-center gap-2">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-8 w-24" />
        </div>
      </div>
    </div>
  );
}

export function TableSkeleton({ rows = 5, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <div className="glass-card p-5">
      <div className="flex items-center gap-3 mb-4">
        <Skeleton className="h-10 flex-1 rounded-xl" />
        <Skeleton className="h-6 w-24 rounded-full" />
      </div>
      <div className="space-y-0">
        {/* Header */}
        <div className="flex gap-4 border-b border-border pb-3 mb-2">
          {Array.from({ length: cols }).map((_, i) => (
            <Skeleton key={i} className="h-3 flex-1" />
          ))}
        </div>
        {/* Rows */}
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex gap-4 py-3 border-b border-border/50">
            {Array.from({ length: cols }).map((_, c) => (
              <Skeleton key={c} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Skeleton className="h-10 w-48 rounded-xl" />
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>

      {/* Risk dashboard */}
      <RiskDashboardSkeleton />

      {/* Gauge + Chart */}
      <div className="grid gap-4 lg:grid-cols-3">
        <ComplianceGaugeSkeleton />
        <ChartSkeleton />
      </div>

      {/* Activity + Calendar */}
      <div className="grid gap-4 lg:grid-cols-3">
        <ActivityFeedSkeleton />
        <CalendarSkeleton />
      </div>
    </div>
  );
}
